import { useNavigate, useLocation } from 'react-router-dom';
import { Button, GoogleButton, Input, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { ArrowRightCircle, Lock, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	clearStepData,
	getStepData,
	saveStepData,
} from '@/utils/localStorageUtils';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';
import {
	clearPublicSchedulingDraftFromLocalStorage,
	buildRegisterComplementNavigationState,
	forwardRegisterFlowNavigateOptions,
} from '@/utils';
import { PLUMAA_SCHEDULING_RETURN_URL_KEY } from '@/constants/schedulingHandoff';
import { E2E_USER_AUTH } from '@/constants/e2eSchedulingTestIds';

const schema = z
	.object({
		email: z.string().max(255, 'Email inválido').email('Email inválido'),
		password: z
			.string()
			.min(6, 'A senha deve ter no mínimo 6 caracteres')
			.max(255, 'A senha deve ter no máximo 255 caracteres')
			.regex(
				/^[0-9A-Za-zÀ-ÖØ-öø-ÿ!@#$%^&*()_+\-= [\]{};':"\\|,.<>/?~`]+$/,
				'A senha contém caracteres inválidos',
			)
			.regex(
				/^(?=.*[A-Za-z])(?=.*\d).+$/,
				'A senha deve conter pelo menos uma letra e um número',
			),
		repeat_password: z.string(),
	})
	.refine((data) => data.password === data.repeat_password, {
		message: 'As senhas não coincidem',
		path: ['repeat_password'],
	});

export const STORAGE_USER_KEYS = {
	USER_AUTH: 'u_auth',
	STEP_INIT: 's_i',
	STEP_COMPLEMENTATION: 's_c',
};

const UserRegister = () => {
	const navigate = useNavigate();
	const location = useLocation();
	type FormData = z.infer<typeof schema>;

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	useEffect(() => {
		const previousStepSavedData = getStepData(
			STORAGE_USER_KEYS.USER_AUTH,
			STORAGE_USER_KEYS.STEP_INIT,
		) as Partial<FormData> | null;

		if (!previousStepSavedData) {
			return;
		}

		if (typeof previousStepSavedData.email === 'string') {
			setValue('email', previousStepSavedData.email, {
				shouldValidate: false,
			});
		}

		if (typeof previousStepSavedData.password === 'string') {
			setValue('password', previousStepSavedData.password, {
				shouldValidate: false,
			});
		}

		if (typeof previousStepSavedData.repeat_password === 'string') {
			setValue('repeat_password', previousStepSavedData.repeat_password, {
				shouldValidate: false,
			});
		}
	}, [setValue]);

	useEffect(() => {
		const clearRegisterDraftOnReload = () => {
			clearStepData(STORAGE_USER_KEYS.USER_AUTH);
		};

		window.addEventListener('beforeunload', clearRegisterDraftOnReload);

		return () => {
			window.removeEventListener(
				'beforeunload',
				clearRegisterDraftOnReload,
			);
		};
	}, []);

	const onSubmit = (data: FormData) => {
		/**
		 * TODO: Implementar verificação de e-mail existente antes de avançar
		 *
		 * mutate(
		 * 	{ email: data.email, password: data.password },
		 * 	{
		 * 		onSuccess: () => {
		 * 			saveStepData(STORAGE_USER_KEYS.USER_AUTH, STORAGE_USER_KEYS.STEP_INIT, data);
		 * 			navigate('/cadastro/info-complemento');
		 * 		},
		 * 		onError: (error) => { // Tratar erro de e-mail duplicado aqui }
		 * 	}
		 * );
		 */

		// Cadastro pela rota /cadastro: não herdar rascunho antigo, exceto retorno explícito do fluxo de agendamento.
		const preserveSchedulingDraft =
			typeof window !== 'undefined' &&
			sessionStorage.getItem(PLUMAA_SCHEDULING_RETURN_URL_KEY) != null;
		if (!preserveSchedulingDraft) {
			clearPublicSchedulingDraftFromLocalStorage();
		}

		// Fluxo temporário: Salva os dados localmente e avança sem registrar no banco ainda
		saveStepData(
			STORAGE_USER_KEYS.USER_AUTH,
			STORAGE_USER_KEYS.STEP_INIT,
			data,
		);
		navigate(
			'/cadastro/info-complemento',
			buildRegisterComplementNavigationState(location.state, {
				email: data.email,
				password: data.password,
				repeat_password: data.repeat_password,
			}),
		);
	};

	const iconCommonClass = 'w-4 h-4 text-stone-400';

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Crie sua conta | Plumaa</title>
				<meta
					name="description"
					content="Cadastre-se na Plumaa. Encontre psicólogos qualificados e cuide da sua saúde mental com segurança e praticidade."
				/>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
							Crie sua conta
						</h1>
						<p className="text-[10px] font-medium text-stone-400 md:text-xs">
							Insira seus dados para começar
						</p>
					</div>

					<div className="w-full">
						<div className="flex w-full justify-center gap-1 rounded-lg bg-stone-100 p-1">
							<button
								type="button"
								className="w-full cursor-pointer rounded-md border-none bg-white px-0 py-1.5 text-center text-xs font-bold text-stone-900 shadow-sm focus:outline-none"
							>
								Cadastro
							</button>
							<button
								type="button"
								className="w-full cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-xs font-bold text-stone-500 focus:outline-none"
								onClick={() =>
									navigate('/login', {
										...forwardRegisterFlowNavigateOptions(
											location.state,
										),
									})
								}
							>
								Entrar
							</button>
						</div>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						<div className="space-y-3">
							<div className="space-y-1">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									E-MAIL
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Mail className={iconCommonClass} />}
									error={errors.email?.message}
									value={watch('email')}
									displayError
								>
									<Input
										{...register('email')}
										data-testid={
											E2E_USER_AUTH.REGISTER_EMAIL
										}
										placeholder="nome@exemplo.com"
										className="h-9 border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									SENHA
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.password?.message}
									value={watch('password')}
									displayError
								>
									<Input
										type="password"
										{...register('password')}
										data-testid={
											E2E_USER_AUTH.REGISTER_PASSWORD
										}
										placeholder="********"
										className="h-9 border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<label className="ml-1 text-[10px] font-black uppercase tracking-widest text-stone-500">
									CONFIRME A SENHA
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.repeat_password?.message}
									value={watch('repeat_password')}
									displayError
								>
									<Input
										type="password"
										{...register('repeat_password')}
										data-testid={
											E2E_USER_AUTH.REGISTER_PASSWORD_REPEAT
										}
										placeholder="********"
										className="h-9 border-stone-200 text-sm focus:border-stone-400"
									/>
								</InputWrapperAuth>
							</div>
						</div>

						<Button
							type="submit"
							data-testid={E2E_USER_AUTH.REGISTER_SUBMIT}
							className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-stone-800"
							text="Cadastrar"
							endIcon={
								<ArrowRightCircle size={14} className="ml-1" />
							}
						/>
					</form>

					<div className="space-y-4">
						<div className="relative flex items-center">
							<div className="flex-grow border-t border-stone-100"></div>
							<span className="mx-4 flex-shrink text-[10px] font-black uppercase tracking-widest text-stone-300">
								ou
							</span>
							<div className="flex-grow border-t border-stone-100"></div>
						</div>

						<GoogleButton
							className="w-full max-w-none rounded-lg border-stone-200 py-2.5 text-xs hover:bg-stone-50"
							onClick={() => {
								const baseUrl =
									import.meta.env.VITE_BACKEND_URL.replace(
										/\/$/,
										'',
									);
								window.location.href = `${baseUrl}/user/login/oauth`;
							}}
						/>
					</div>

					<div className="border-t border-stone-50 pt-2 text-center">
						<p className="text-[10px] font-medium text-stone-400">
							Já possui uma conta?{' '}
							<button
								onClick={() =>
									navigate('/login', {
										...forwardRegisterFlowNavigateOptions(
											location.state,
										),
									})
								}
								className="font-black text-stone-900 underline-offset-4 hover:underline"
							>
								Fazer login
							</button>
						</p>
					</div>
				</div>
			</main>
		</div>
	);
};

export { UserRegister };
